/**
 * Build stacked <thead> row model from TanStack leaf columns (API headers + colspan + rowspan).
 * Only `column.id` and `column.columnDef` are used; TValue is unconstrained.
 * @param {import('@tanstack/react-table').Column<TableRowFlattened, unknown>[]} leafColumns
 * @returns {BuiltHeadRow[]}
 */

/**
 * @typedef {object} BuiltHeadCell
 * @property {'api' | 'leaf' | 'checkbox-span'} kind
 * @property {number} colspan
 * @property {number} rowspan
 * @property {number} leafStartIndex - First visible leaf column index covered by this `<th>` (0-based)
 * @property {string} columnId
 * @property {ApiManagerTableColumnHeader} header
 */

/**
 * @typedef {object} BuiltHeadRow
 * @property {string} rowKey - Stable id for React list keys (derived from cell layout)
 * @property {BuiltHeadCell[]} cells
 */

/**
 * @param {BuiltHeadCell[]} cells
 * @returns {string}
 */
function builtHeadRowKey(cells) {
  return cells.map((c) => `${c.columnId}:${c.kind}:${c.colspan}:${c.rowspan}`).join('|');
}

/**
 * @param {TableColumnsReactTable} columnDef
 * @returns {ApiManagerTableColumnHeader[]}
 */
function getNormalizedHeaders(columnDef) {
  if (columnDef.headers?.length) {
    return columnDef.headers;
  }
  return [{
    lib: columnDef.lib ?? '',
    width: columnDef.width ?? 'auto',
    colspan: 1,
    align: /** @type {'left' | 'center' | 'right'} */ (columnDef.align ?? 'left'),
    backgroundColor: null,
    textDecoration: { strikeThrough: false, underline: false },
  }];
}

/**
 * @param {import('@tanstack/react-table').Column<TableRowFlattened, unknown>} column
 * @returns {number}
 */
function headerDepth(column) {
  return getNormalizedHeaders(/** @type {TableColumnsReactTable} */ (column.columnDef)).length;
}

/**
 * @param {import('@tanstack/react-table').Column<TableRowFlattened, unknown>[]} leafColumns
 * @returns {BuiltHeadRow[]}
 */
export function buildTableHeadRows(leafColumns) {
  if (!leafColumns.length) {
    return [];
  }

  const maxR = Math.max(1, ...leafColumns.map(headerDepth));
  const n = leafColumns.length;

  /** @type {BuiltHeadRow[]} */
  const rows = [];
  /** @type {number[]} */
  const rowspanRemaining = Array.from({ length: n }, () => 0);

  for (let r = 0; r < maxR; r += 1) {
    /** @type {BuiltHeadCell[]} */
    const cells = [];
    let i = 0;

    while (i < n) {
      if (rowspanRemaining[i] > 0) {
        rowspanRemaining[i] -= 1;
        i += 1;
        // eslint-disable-next-line no-continue
        continue;
      }

      const { id: columnId, columnDef: columnDefRaw } = leafColumns[i];
      const columnDef = /** @type {TableColumnsReactTable} */ (columnDefRaw);
      const headers = getNormalizedHeaders(columnDef);
      const hlen = headers.length;
      const isCheckbox = columnDef.type === 'static-checkbox';

      if (hlen === 1 && maxR > 1 && r === 0) {
        const cellHeader = headers[0];
        const cs = Math.max(1, cellHeader.colspan ?? 1);
        cells.push({
          kind: isCheckbox ? 'checkbox-span' : 'api',
          colspan: cs,
          rowspan: maxR,
          leafStartIndex: i,
          columnId,
          header: cellHeader,
        });
        for (let j = 0; j < cs; j += 1) {
          rowspanRemaining[i + j] = maxR - 1;
        }
        i += cs;
        // eslint-disable-next-line no-continue
        continue;
      }

      if (hlen === maxR) {
        const cellHeader = headers[r];
        const cs = Math.max(1, cellHeader.colspan ?? 1);
        const isLastRow = r === maxR - 1;
        cells.push({
          kind: isLastRow ? 'leaf' : 'api',
          colspan: cs,
          rowspan: 1,
          leafStartIndex: i,
          columnId,
          header: cellHeader,
        });
        i += cs;
        // eslint-disable-next-line no-continue
        continue;
      }

      if (hlen === 1 && maxR === 1) {
        const cellHeader = headers[0];
        const cs = Math.max(1, cellHeader.colspan ?? 1);
        cells.push({
          kind: 'leaf',
          colspan: cs,
          rowspan: 1,
          leafStartIndex: i,
          columnId,
          header: cellHeader,
        });
        i += cs;
        // eslint-disable-next-line no-continue
        continue;
      }

      // Mismatch depth (e.g. hlen 2, maxR 5): fill with last available header row per row index
      const safeR = Math.min(r, hlen - 1);
      const cellHeader = headers[safeR];
      const cs = Math.max(1, cellHeader.colspan ?? 1);
      const isLastRow = r === maxR - 1;
      cells.push({
        kind: isLastRow ? 'leaf' : 'api',
        colspan: cs,
        rowspan: 1,
        leafStartIndex: i,
        columnId,
        header: cellHeader,
      });
      i += cs;
    }

    rows.push({ rowKey: builtHeadRowKey(cells), cells });
  }

  return rows;
}
